import { jsLazyLoad } from '@elab/core/utils/webComponent';

/**
 * Check if number is correct and between min and max
 * @param {Number} num - number to check
 * @param {Number} min - min number value
 * @param {Number} max - max number value
 * @returns {Boolean} - result of number check
 */
export const isNumberInRange = (num, min, max) => typeof num === 'number' && num > min && num < max;
export const isNumberInRangeSoft = (num, min, max) =>
    typeof num === 'number' && num >= min && num <= max;

/**
 * return correct location format if data is valid
 * @param {Object} store - point object with geo information
 * @returns {{lat: *, lng: *}|null} - format that is used in google maps
 */
export const getPointCoordinates = ({ geoPoint = {} } = {}) => {
    if (
        !isNumberInRange(geoPoint.latitude, -90, 90) ||
        !isNumberInRange(geoPoint.longitude, -180, 180)
    ) {
        return null;
    }

    return {
        lat: geoPoint.latitude,
        lng: geoPoint.longitude,
    };
};

export const runTaskAsynchronously = callback => {
    setTimeout(callback, 0);
};

/**
 * return FieldValue by storePath
 * @param { String } name - store path, e.g. 'user.profile.email'
 * @param { Object } state - store
 */
export const getValueByStorePath = (name, state) => {
    return name
        .split('.')
        .reduce((acc, storeFieldName) => (acc ? acc[storeFieldName] : undefined), state);
};

export const getStrikeThroughPrice = (priorPriceObj = {}, priceObj = {}) => {
    let strikeThroughPrice = null;
    let currentPrice;
    let oldPrice;
    const priorPrice = priorPriceObj && priorPriceObj.value;
    if (priceObj) {
        currentPrice = priceObj.value;
        oldPrice = priceObj.oldValue;
    }
    if (priorPrice && currentPrice < priorPrice) {
        strikeThroughPrice = priorPriceObj.formattedValue;
    } else if (!priorPrice && oldPrice && oldPrice > currentPrice) {
        strikeThroughPrice = priceObj.formattedOldValue;
    }
    return strikeThroughPrice;
};

/**
 * Helper returns object without ignored properties.
 * @param { Array } ignoreList - array of properties that should be ignored.
 *                  For example: ['ui.loading', 'cart.loading']
 * @param { Object } source - source object
 *
 * @returns { Object } - result object without ignored properties
 */
export const ignoreObjectProperties = (ignoreList, source = {}) => {
    if (!Array.isArray(ignoreList)) {
        return source;
    }

    const resultState = { ...source };

    ignoreList.forEach(propertyPath => {
        let subState = resultState;

        const propertiesChain = propertyPath.split('.');
        const lastPropertyIndex = propertiesChain.length - 1;

        propertiesChain.forEach((property, i) => {
            if (lastPropertyIndex === i) {
                delete subState[property];
            } else if (subState) {
                subState = subState[property];
            }
        });
    });

    return resultState;
};

/**
 * @param { Object } target - object to discard properties from
 * @param { Array } discardedProps - array of property names to discard
 * @returns { Object } new object without props specified in discardedProps argument
 */
export const discardProps = (discardedProps, target) => {
    return Object.keys(target).reduce(
        (acc, key) => (discardedProps.includes(key) ? acc : { ...acc, [key]: target[key] }),
        {},
    );
};

// currying of the previous one for using in Array.map
export const getValueByStorePathInStore = state => name => getValueByStorePath(name, state);

/**
 * @param { * } key - key for appropriate default value
 * @param { * } defaultValue - common defaultValue
 * @param { Object } options - object for custom pair key - default value
 * @returns { * } defined defaultValue
 */
export const getDefaultValue = (key, defaultValue, options) => {
    return options && Object.keys(options).includes(key) ? options[key] : defaultValue;
};

const calledPromises = {};
export const loadAsset = assetName => {
    if (calledPromises[assetName]) {
        return calledPromises[assetName];
    }

    calledPromises[assetName] = new Promise(resolve => {
        const assetUrl = window.e2_assets_urls[assetName];
        jsLazyLoad(assetUrl).then(resolve);
    });

    return calledPromises[assetName];
};

export const camelCaseToKebabCase = name => {
    return name.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $.toLowerCase());
};

export function getAssets() {
    return window.e2_assets;
}

export function createBulkAddToBagPayload(productData, productQty) {
    const orderEntries = [];
    const code = productData.code;
    const { productReferences = {} } = productData;
    for (let product of productReferences) {
        orderEntries.push({
            code: product.target.code,
            quantity: product.quantity * productQty,
        });
    }
    return { orderEntries, code, productBundle: true, quantityAdded: productQty };
}

export function base64ToFile(base64String, fileName, mimeType) {
    const byteCharacters = atob(base64String);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: mimeType });
    return new File([blob], fileName, { type: mimeType });
}

export function checkTimeDifference(timestamp = '', intervalToCheck) {
    if (!timestamp) {
        return true;
    }
    const normalizedTimestamp = timestamp.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');

    const givenTime = new Date(normalizedTimestamp);

    if (isNaN(givenTime.getTime())) {
        throw new Error('Invalid timestamp format');
    }

    const now = new Date();
    const differenceInMs = now - givenTime;
    const differenceInDays = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));

    return differenceInDays >= intervalToCheck;
}

export function getCustomTimestamp() {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    const timezoneOffset = now.getTimezoneOffset();
    const offsetSign = timezoneOffset <= 0 ? '+' : '-';
    const offsetHours = String(Math.floor(Math.abs(timezoneOffset) / 60)).padStart(2, '0');
    const offsetMinutes = String(Math.abs(timezoneOffset) % 60).padStart(2, '0');

    const timestamp = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}${offsetMinutes}`;
    return timestamp;
}

export const hashString = async input => {
    if (!input) {
        return;
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(input);

    // Use the Web Crypto API to compute the SHA-256 hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    // Convert the hash ArrayBuffer to a hexadecimal string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');

    return hashHex;
};

export default { getAssets, loadAsset };
